import { lazy, StrictMode, Suspense } from 'react';

import ReactDOM from 'react-dom/client';

import '@/styles/entry.css';

import { keepPreviousData, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { AuthProvider } from 'react-oidc-context';
import { BrowserRouter } from 'react-router-dom';

import App from './App';
/* We import this to trigger the env validation */
import '../env';

import { AppBootstrap } from './components/app-bootstrap';
import { MaintenanceLockout } from './components/maintenance-lockout';
import { onSigninCallback, userManager } from './lib/fusionauth';
import { MAINTENANCE_MODE_ENABLED } from './maintenance';
import { ErrorBoundary } from './services/bugsnag';

// Dynamic import keeps the dev overlay (and its side-effectful ws-client module)
// out of the production graph structurally — the import() lives in a dead branch
// under `import.meta.env.DEV`, so Rollup never emits the chunk.
// eslint-disable-next-line react-refresh/only-export-components -- entry module, not an HMR boundary
const DevClaudeOverlay = import.meta.env.DEV ? lazy(() => import('./dev/dev-claude-overlay').then((module) => ({ default: module.DevClaudeOverlay }))) : null;

dayjs.extend(relativeTime);
dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      placeholderData: keepPreviousData,
    },
  },
});

const rootElement = document.getElementById('root')!;
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);

  // Ahead of auth/bootstrap: the API is down during maintenance, so the user fetch would hit the error screen.
  const appTree = MAINTENANCE_MODE_ENABLED ? (
    <MaintenanceLockout />
  ) : (
    <AuthProvider userManager={userManager} onSigninCallback={onSigninCallback}>
      <BrowserRouter>
        <AppBootstrap>
          <App />
        </AppBootstrap>
      </BrowserRouter>
    </AuthProvider>
  );

  root.render(
    <ErrorBoundary>
      <StrictMode>
        <QueryClientProvider client={queryClient}>
          {/* Dev-only: an inner boundary contains app render crashes so the sibling dev overlay survives to switch off a broken branch. Folds to `appTree` in prod, leaving the production tree unchanged. */}
          {import.meta.env.DEV ? <ErrorBoundary>{appTree}</ErrorBoundary> : appTree}
          <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
          {DevClaudeOverlay ? (
            <Suspense fallback={null}>
              <DevClaudeOverlay />
            </Suspense>
          ) : null}
        </QueryClientProvider>
      </StrictMode>
    </ErrorBoundary>,
  );
}
